// userService.jsx
import api from './api';

export class UserService {
    static async getUserProfile(userId) {
        // Add validation for userId
        if (!userId || userId === '[object Object]' || typeof userId === 'object') {
            console.warn('Invalid userId provided to getUserProfile:', userId);
            return null;
        }

        try {
            const response = await api.get(`/api/users/${userId}/`);
            return response.data;
        } catch (error) {
            console.warn(`Failed to fetch user profile for ID ${userId}:`, error);
            return null; // Return null instead of throwing error for graceful degradation
        }
    }
    
    static async updateUserProfile(userId, profileData) {
        try {
            const response = await api.put(`/api/users/${userId}/`, profileData);
            return response.data;
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    }

    static async changePassword(passwordData) {
        try {
            // Create a copy to avoid mutating the original object
            const payload = { ...passwordData };
            
            // Get the current user's email if not provided
            if (!payload.email) {
                // Try to get from local storage
                const userJSON = localStorage.getItem('user');
                if (userJSON) {
                    try {
                        const userData = JSON.parse(userJSON);
                        if (userData && userData.email) {
                            payload.email = userData.email;
                            console.log('[USER SERVICE] Using email from localStorage user:', payload.email);
                        }
                    } catch (e) {
                        console.error('[USER SERVICE] Error parsing user JSON:', e);
                    }
                }
                
                // If still no email, try JWT token
                if (!payload.email) {
                    const token = localStorage.getItem('authToken');
                    if (token) {
                        try {
                            // Extract payload from JWT (middle part between dots)
                            const tokenParts = token.split('.');
                            if (tokenParts.length >= 2) {
                                const base64Payload = tokenParts[1];
                                // Add padding if needed
                                const paddedBase64 = base64Payload.padEnd(base64Payload.length + (4 - base64Payload.length % 4) % 4, '=');
                                // Decode base64
                                const decodedString = atob(paddedBase64.replace(/-/g, '+').replace(/_/g, '/'));
                                const decodedPayload = JSON.parse(decodedString);

                                // Check all possible fields where email might be stored
                                if (decodedPayload.email) {
                                    payload.email = decodedPayload.email;
                                } else if (decodedPayload.user_email) {
                                    payload.email = decodedPayload.user_email;
                                } else if (decodedPayload.sub) {
                                    payload.email = decodedPayload.sub;
                                } else if (decodedPayload.user_id) {
                                    payload.email = decodedPayload.user_id;
                                }
                            }
                        } catch (tokenError) {
                            console.error('[USER SERVICE] Error parsing JWT token:', tokenError);
                        }
                    }
                }
            }
            
            if (!payload.email) {
                console.error('[USER SERVICE] No email found for password change request');
                throw new Error('Email is required for password change');
            }
            
            const response = await api.post('/api/change-password/', payload);
            console.log('[USER SERVICE] Password change successful');
            return response.data;
        } catch (error) {
            console.error('[USER SERVICE] Error changing password:', error.message || 'unknown error');
            throw error;
        }
    }

    static async addUser(userData) {
        try {
            console.log('Adding user with data:', userData);
            
            // Handle internal vs external users based on form data
            const requestData = {
                email: userData.email,
                // backend expects camelCase field names
                firstName: userData.firstName,
                lastName: userData.lastName,
                // Password will be generated on the server and emailed to the user
                role: userData.role,
                is_internal: userData.is_internal || false, // Use form data for is_internal flag
                generate_password: true, // Flag to indicate password should be generated server-side
                study_id: parseInt(userData.study_id) // jdt - link user to study
            };

            // Add site assignments if provided
            if (userData.site_ids && userData.site_ids.length > 0) {
                requestData.site_ids = userData.site_ids;
                console.log('Adding site_ids to request:', userData.site_ids);
            }
            
            // Handle single site assignment (for site-specific user addition)  
            if (userData.site_id) {
                requestData.site_id = userData.site_id;
                console.log('Adding site_id to request:', userData.site_id);
            }
            
            // Try to get sponsor_id from userData
            if (userData.sponsor_id) {
                console.log('Setting sponsor_id from form data:', userData.sponsor_id);
                requestData.sponsor_id = userData.sponsor_id;
            } else {
                // Fallback: Try to extract from JWT token
                const token = localStorage.getItem('authToken');
                if (token) {
                    try {
                        // Extract payload from JWT (middle part between dots)
                        const payload = token.split('.')[1];
                        // Decode base64
                        const decodedPayload = JSON.parse(atob(payload));
                        if (decodedPayload.sponsor_id) {
                            requestData.sponsor_id = decodedPayload.sponsor_id;
                        }
                    } catch (tokenError) {
                        console.error('Error parsing JWT token:', tokenError);
                    }
                }
            }

            const response = await api.post('/api/register/', requestData);
            return response.data;
        } catch (error) {
            console.error('Error adding user:', error);
            if (error.response?.data) {
                // Format validation errors from the API
                const errors = Object.entries(error.response.data)
                    .map(([field, messages]) => {
                        // If it's a list of errors, join them
                        const messageStr = Array.isArray(messages) ? messages.join(', ') : messages;
                        return `${field}: ${messageStr}`;
                    })
                    .join('\n');
                throw new Error(errors || 'Unable to create account.');
            }
            throw new Error('Unable to create account.');
        }
    }

    static async getUsers() {
        try {
            const response = await api.get('/api/users/');
            return response.data;
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    }

    static async assignSponsor(userId, sponsorId) {
        try {
            const response = await api.post(`/api/users/${userId}/assign-sponsor/`, {
                sponsor_id: sponsorId
            });
            return response.data;
        } catch (error) {
            console.error('Error assigning sponsor to user:', error);
            throw error;
        }
    }

    static async assignStudy(userId, studyId) {
        try {
            const response = await api.post(`/api/users/${userId}/assign-study/`, {
                study_id: studyId
            });
            return response.data;
        } catch (error) {
            console.error('Error assigning study to user:', error);
            throw error;
        }
    }

    static async assignSite(userId, siteId) {
        try {
            const response = await api.post(`/api/users/${userId}/assign-site/`, {
                site_id: siteId
            });
            return response.data;
        } catch (error) {
            console.error('Error assigning site to user:', error);
            throw error;
        }
    }

    static getRoles(user) {
        const role = user?.role;

        if (role == 'Program Admin' || role == 'Project Admin') {
            return [
                { value: 'program_admin', label: 'Program Admin' },
                { value: 'data_manager', label: 'Data Manager' },
                { value: 'study_team', label: 'Study Team' },
                { value: 'study_coordinator', label: 'Study Coordinator' },
                { value: 'investigator', label: 'Investigator' },
                { value: 'cra', label: 'CRA' }
            ];
        }


        if (role == 'Study Team' ) {
            return [
                { value: 'study_team', label: 'Study Team' },
                { value: 'study_coordinator', label: 'Study Coordinator' },
                { value: 'investigator', label: 'Investigator' },
                { value: 'cra', label: 'CRA' }
            ];
        }

        return [];

    }
}

export default UserService;